import { Link } from "@tanstack/react-router";

import { Badge } from "@/components/Badge";
import Icon from "@/components/Icon/Icon";
import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import type { WorkerType } from "@/constants/workerType";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { LabelPerson } from "../LabelPerson";

export type PersonPreviewData = {
  /**
   * 人物ID
   */
  personId?: string;
  /**
   * 人物名
   */
  personName?: string;
  /**
   * 従業員番号
   */
  workerNumber?: string;
  /**
   * アバターURL
   */
  avatarUrl?: string;
  /**
   * メールアドレス
   */
  email?: string;
  /**
   * ワーカータイプ（社員、契約社員など）
   */
  workerType?: WorkerType;
};

type Props = {
  /**
   * 人物情報
   */
  person: PersonPreviewData;
  /**
   * 「プロフィールを見る」リンクの表示可否
   */
  showProfileLink?: boolean;
  /**
   * リンク表示テキスト
   */
  linkText?: string;
  /**
   * クラス名
   */
  className?: string;
};

/**
 * 人物プレビューカードのコンテンツコンポーネント
 */
export function PersonPreviewContent({
  person,
  showProfileLink = true,
  linkText,
  className,
}: Props) {
  const { t } = useTranslation(defaultNS);

  return (
    <VStack className={className}>
      <VStack className="gap-2 max-w-2xl">
        {person.workerType && (
          <HStack className="items-center">
            <Badge variant="primaryMuted">
              {t(
                `domain.workerTypeRule.common.workerType.${person.workerType}`,
              )}
            </Badge>
          </HStack>
        )}
        <HStack className="items-center gap-2">
          <LabelPerson
            name={person.personName ?? ""}
            avatarUrl={person.avatarUrl}
            size="Default"
            hoverScale={false}
          />
          {person.workerNumber && (
            <Text variant="label">{person.workerNumber}</Text>
          )}
        </HStack>
        {person.email && (
          <HStack className="items-center gap-2">
            <Icon iconName="Mail" />
            <Text variant="label" className="truncate">
              {person.email}
            </Text>
          </HStack>
        )}
      </VStack>
      {showProfileLink && person.personId && (
        <HStack className="mt-2 justify-end">
          <Link
            to="/person/$id"
            params={{ id: person.personId }}
            className="flex items-center justify-center font-semibold text-muted-foreground hover:text-primary"
          >
            {linkText || t("component.PersonPreviewContent.goToProfile")}
          </Link>
        </HStack>
      )}
    </VStack>
  );
}
